// Client for the baba-v2 gateway's POST /draft-replies — two ready-to-send
// Hebrew replies (plus a meaning gloss each) for a Hebrew message the
// learner just translated (translation direction he-to-other, i.e. a
// message they RECEIVED).
//
// This mirrors the mobile client (baba-v2 repo's
// mobile/services/translation/draftReplies.ts) and the authority at
// server/draft-replies/policy.ts exactly: same free-tier limit (3 per
// 30-day window), same 300-character cap on the message being replied to,
// same `code: 'upgrade_required'` 429 signal, same "never charge for fewer
// than two usable options" contract.
//
// `/draft-replies` is mounted at the gateway WORKER ROOT (same as `/chat`
// and `/chat/title` — see baba-v2's cloudflare-worker/src/index.ts), NOT
// under `/api/v1`, so this talks to the worker origin directly rather than
// going through `gatewayFetch`/`gatewayFetchJson` (which target
// `BABA_GATEWAY_BASE_URL`, the `.../api/v1` prefix). Mirrors
// apps/web/src/components/chat/gatewayChat.ts's own `CHAT_WORKER_ORIGIN`
// derivation for the identical reason, computed locally here rather than
// imported so this module has no dependency on chat/**.
import { BABA_GATEWAY_BASE_URL, GatewayAuthError, GatewayError } from '@/lib/gateway'
import { getAuthToken } from '@/lib/baba-auth'

const REPLY_DRAFTS_WORKER_ORIGIN = BABA_GATEWAY_BASE_URL.replace(/\/api\/v1\/?$/, '')

/** Free tier: three successful generations per 30-day window. MIRROR of
 *  the baba-v2 repo's server/draft-replies/policy.ts FREE_REPLY_DRAFT_LIMIT
 *  (that module is the authority; this repeats the number so a signed-in
 *  free user who is out of drafts can be told that directly). */
export const FREE_REPLY_DRAFT_LIMIT = 3

/** Longest incoming message we offer drafts for — the same 300 characters
 *  server/draft-replies/policy.ts's MAX_DRAFTABLE_CHARACTERS enforces. */
export const MAX_DRAFTABLE_CHARACTERS = 300

export interface ReplyDraftOption {
  hebrew: string
  gloss: string
}

export interface ReplyDraftsUsage {
  used: number
  /** null = unlimited (Pro / Pro+ / reverse trial). */
  limit: number | null
}

/** Is this result the kind of thing a learner would reply to? Mirrors
 *  server/draft-replies/policy.ts's isDraftableLength. */
export function isDraftableLength(text: string): boolean {
  const trimmed = text.trim()
  return trimmed.length > 0 && trimmed.length <= MAX_DRAFTABLE_CHARACTERS
}

/**
 * Thrown when the free tier has no drafts left for this window — the one
 * failure that belongs on the paywall instead of an error message. Mirrors
 * mobile's `ReplyDraftsQuotaError`.
 */
export class ReplyDraftsQuotaError extends Error {
  readonly code = 'upgrade_required'
}

export interface DraftRepliesParams {
  /** The incoming Hebrew message. */
  sourceText: string
  /** Its translation, in the learner's language — what they actually read. */
  translatedText: string
  /** Target-language code, e.g. 'en'. */
  language: string
  /** Target language's readable name, e.g. 'French' — server/draft-replies
   *  falls back to `language` only when this is absent, and it's what the
   *  prompt actually tells the model to write the gloss in. */
  languageNative?: string
  /** Optional: what the learner said they want to reply, in their own words.
   *  When present the two options become two PHRASINGS of this same answer
   *  instead of two opposing guesses — see server/draft-replies/prompt.ts. */
  intent?: string
  genderContext?: string
  userGender?: 'male' | 'female'
  abortSignal?: AbortSignal
}

export interface DraftRepliesResult {
  options: ReplyDraftOption[]
  /** Server-authoritative counter after the charge. null limit = unlimited. */
  usage?: ReplyDraftsUsage
}

/**
 * Web's own addressee-gender vocabulary (`GenderId` in apps/web/lib/baba-api.ts
 * — 'to_male', 'to_female', …) mapped to the 'speaking-to-*' vocabulary that
 * server/translate/utils/audience.ts's `mapGenderContextToAudience` actually
 * recognizes for draft-replies. Unlike `/chat` (see gatewayChat.ts's own
 * comment on `addresseeGender`), draft-replies does NOT alias the web's
 * tokens server-side, so sending 'to_male' straight through would silently
 * fall back to a gender-neutral reply — this mapping has to happen here.
 * Anything unrecognized (including 'general' and 'to_mixed_group', which has
 * no audience.ts equivalent) returns undefined, which is the same "general"
 * fallback the server itself applies to an absent/unknown value.
 */
export function mapAddresseeGenderContext(genderId: string | undefined): string | undefined {
  switch (genderId) {
    case 'to_male': return 'speaking-to-man'
    case 'to_female': return 'speaking-to-woman'
    case 'to_male_group': return 'speaking-to-men-group'
    case 'to_female_group': return 'speaking-to-women-group'
    default: return undefined
  }
}

/** Web's speaker-gender ids ('male' | 'female' from `GenderId`) as the
 *  'male' | 'female' the prompt expects. Anything else (including 'general',
 *  the default) falls back to 'male' — matching the server's own default in
 *  server/draft-replies/prompt.ts's `getSpeakerGenderInfo`. */
export function mapSpeakerGender(genderId: string | undefined): 'male' | 'female' {
  return genderId === 'female' ? 'female' : 'male'
}

async function replyDraftsFetch(init: RequestInit): Promise<Response> {
  const token = getAuthToken()
  if (!token) throw new GatewayAuthError('No baba session - sign in first')
  const headers = new Headers(init.headers)
  headers.set('Authorization', `Bearer ${token}`)
  headers.set('Content-Type', 'application/json')
  return fetch(`${REPLY_DRAFTS_WORKER_ORIGIN}/draft-replies`, { ...init, headers })
}

/**
 * POST /draft-replies — two sendable Hebrew replies plus a gloss for each.
 *
 * Throws rather than returning null: there is no useful fallback here, and
 * the sheet has to tell the learner what happened. `ReplyDraftsQuotaError`
 * is separated out because it is the one failure that belongs on the
 * paywall instead of an error message — mirrors the mobile client exactly.
 *
 * A 401 from the gateway is surfaced as `GatewayAuthError` same as
 * `gatewayFetchJson`'s own 401 handling, but note that reaching this
 * function at all already implies `getAuthToken()` returned a token: for a
 * signed-in web user, a 401 here means the gateway rejected THAT token (see
 * apps/web/lib/gateway.ts's header comment on the web<->gateway identity
 * bridge), not that there is no session — callers should treat it as
 * "feature unavailable right now", not "sign in again".
 */
export async function draftReplies(params: DraftRepliesParams): Promise<DraftRepliesResult> {
  const response = await replyDraftsFetch({
    method: 'POST',
    body: JSON.stringify({
      sourceText: params.sourceText,
      translatedText: params.translatedText,
      language: params.language,
      languageNative: params.languageNative,
      intent: params.intent?.trim() || undefined,
      genderContext: params.genderContext,
      userGender: params.userGender,
      platform: 'web',
    }),
    signal: params.abortSignal,
  })

  const data = (await response.json().catch(() => ({}))) as {
    options?: ReplyDraftOption[]
    usage?: ReplyDraftsUsage
    error?: string
    code?: string
  }

  if (response.status === 429 && data.code === 'upgrade_required') {
    throw new ReplyDraftsQuotaError(data.error || 'Free reply drafts used.')
  }
  if (response.status === 401) {
    throw new GatewayAuthError(data.error || 'Reply drafts are not available yet.', data.code ?? null)
  }
  if (!response.ok) {
    throw new GatewayError(data.error || `Could not draft replies right now. (${response.status})`, response.status, data.code ?? null)
  }

  const options = Array.isArray(data.options)
    ? data.options.filter((o): o is ReplyDraftOption => typeof o?.hebrew === 'string' && typeof o?.gloss === 'string')
    : []
  // The server refuses to charge for fewer than two — see policy.ts's
  // parseDraftOptions in the baba-v2 repo — so this is a transport anomaly,
  // not a normal outcome.
  if (options.length < 2) {
    throw new GatewayError('Could not draft replies for this message.', response.status, 'unusable')
  }

  return { options: options.slice(0, 2), usage: data.usage }
}
